import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Download, Send, RefreshCw, BellRing, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { eurf } from "./shared";

export default function FacturationView({ token }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState("");
  const [dunning, setDunning] = useState({ enabled: true, days: 7, repeat_days: 7 });
  const hdr = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => axios.get(`${API}/superadmin/subscription-invoices`, hdr).then(({ data }) => setRows(data || [])).catch(() => {});
  const loadDunning = () => axios.get(`${API}/superadmin/subscription-invoices/dunning-settings`, hdr).then(({ data }) => setDunning(data)).catch(() => {});
  useEffect(() => { load(); loadDunning(); }, []); // eslint-disable-line

  const saveDunning = async (patch) => {
    const next = { ...dunning, ...patch };
    setDunning(next);
    try { const { data } = await axios.put(`${API}/superadmin/subscription-invoices/dunning-settings`, next, hdr); setDunning(data); toast.success("Relances mises à jour"); }
    catch { toast.error("Erreur"); }
  };

  const generate = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/superadmin/subscription-invoices/generate`, {}, hdr); toast.success(`${data.generated} facture(s) générée(s)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const resend = async (id) => {
    try { await axios.post(`${API}/superadmin/subscription-invoices/${id}/resend`, {}, hdr); toast.success("Facture renvoyée par email"); load(); }
    catch { toast.error("Erreur"); }
  };
  const togglePaid = async (id) => {
    try { const { data } = await axios.post(`${API}/superadmin/subscription-invoices/${id}/toggle-paid`, {}, hdr); toast.success(data.paid ? "Facture marquée payée" : "Facture marquée impayée"); load(); }
    catch { toast.error("Erreur"); }
  };
  const pdf = (id) => window.open(`${API}/superadmin/subscription-invoices/${id}/pdf`, "_blank");

  return (
    <div className="space-y-5" data-testid="sa-facturation">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Facturation des abonnements</h1>
          <p className="mt-1 text-sm text-zinc-500">Factures générées automatiquement chaque mois et envoyées par email aux prestataires abonnés.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="sa-export-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-full border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-300" />
          <Button data-testid="sa-export-csv" onClick={() => window.open(`${API}/superadmin/subscription-invoices/export${month ? `?period=${month}` : ""}`, "_blank")} variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800">
            <Download className="mr-1.5 h-4 w-4" />Export CSV
          </Button>
          <Button data-testid="sa-export-zip" onClick={() => window.open(`${API}/superadmin/subscription-invoices/export-zip${month ? `?period=${month}` : ""}`, "_blank")} variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800">
            <Download className="mr-1.5 h-4 w-4" />Export ZIP (PDF + récap)
          </Button>
          <Button data-testid="sa-gen-invoices" onClick={generate} disabled={busy} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">
            <RefreshCw className="mr-1.5 h-4 w-4" />{busy ? "Génération…" : "Générer ce mois"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid="sa-dunning-settings">
        <span className="flex items-center gap-2 text-sm font-medium text-white"><BellRing className="h-4 w-4 text-[#FF6A00]" />Relances automatiques impayés</span>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input data-testid="sa-dunning-enabled" type="checkbox" checked={!!dunning.enabled} onChange={(e) => saveDunning({ enabled: e.target.checked })} className="h-4 w-4 accent-[#FF6A00]" />
          Activées
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-400">
          Après
          <input data-testid="sa-dunning-days" type="number" min="1" value={dunning.days} onChange={(e) => setDunning({ ...dunning, days: Number(e.target.value) })} onBlur={(e) => saveDunning({ days: Number(e.target.value) })} className="w-16 rounded-lg border border-zinc-800 bg-zinc-950 px-2 py-1 text-center text-white" />
          jours
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-400">
          puis tous les
          <input data-testid="sa-dunning-repeat" type="number" min="1" value={dunning.repeat_days} onChange={(e) => setDunning({ ...dunning, repeat_days: Number(e.target.value) })} onBlur={(e) => saveDunning({ repeat_days: Number(e.target.value) })} className="w-16 rounded-lg border border-zinc-800 bg-zinc-950 px-2 py-1 text-center text-white" />
          jours
        </label>
        <span className="text-xs text-zinc-500">Modifiez le design du mail dans « Personnalisation ».</span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-16 text-center" data-testid="sa-facturation-empty">
          <FileText className="mx-auto h-12 w-12 text-zinc-700" />
          <p className="mt-4 font-medium text-white">Aucune facture pour le moment</p>
          <p className="mt-1 text-sm text-zinc-500">Les factures des abonnements actifs apparaîtront ici. Renseignez vos infos légales dans Paramètres.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-900/60 text-xs uppercase text-zinc-500">
              <tr><th className="px-4 py-3">N°</th><th className="px-4 py-3">Prestataire</th><th className="px-4 py-3">Période</th><th className="px-4 py-3 text-right">HT</th><th className="px-4 py-3 text-right">TTC</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Envoyée</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`sa-invoice-${r.id}`} className="border-t border-zinc-800/60">
                  <td className="px-4 py-3 font-medium text-white">{r.number}</td>
                  <td className="px-4 py-3 text-zinc-300">{r.org_name}</td>
                  <td className="px-4 py-3 text-zinc-400">{r.period}</td>
                  <td className="px-4 py-3 text-right text-zinc-400">{eurf(r.amount_ht)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-white">{eurf(r.amount_ttc)}</td>
                  <td className="px-4 py-3">
                    {r.paid_at
                      ? <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-400">Payée</span>
                      : (r.overdue_reminder_count > 0
                          ? <span className="rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-400">En retard</span>
                          : <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-400">Impayée</span>)}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{r.sent_at ? "Oui" : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`sa-invoice-paid-${r.id}`} onClick={() => togglePaid(r.id)} title={r.paid_at ? "Marquer impayée" : "Marquer payée"} className={`rounded-full border p-1.5 ${r.paid_at ? "border-emerald-700 text-emerald-400 hover:bg-emerald-500/10" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>{r.paid_at ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}</button>
                      <button data-testid={`sa-invoice-pdf-${r.id}`} onClick={() => pdf(r.id)} title="Télécharger le PDF" className="rounded-full border border-zinc-700 p-1.5 text-zinc-300 hover:bg-zinc-800"><Download className="h-4 w-4" /></button>
                      <button data-testid={`sa-invoice-resend-${r.id}`} onClick={() => resend(r.id)} title="Renvoyer par email" className="rounded-full border border-zinc-700 p-1.5 text-zinc-300 hover:bg-zinc-800"><Send className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
